import { makeAutoObservable, runInAction } from 'mobx'
import { debounce } from 'lodash'
import { logger } from 'shared/lib'
import { type IBroadcast } from 'entities/Broadcast'
import { BroadcastOneTime } from 'entities/Broadcast/model/BroadcastOneTime'
import { BroadcastRecurring } from 'entities/Broadcast/model/BroadcastRecurring'
import { BroadcastApi } from 'entities/Broadcast/api/broadcast'
import {
  type IResponseBroadcast,
  type IResponseBroadcastById,
  type IResponseRecurringBroadcastById,
} from 'entities/Broadcast/api/types'
import type { EntitiesStore } from 'entities/EntitiesExport'

export class BroadcastStore {
  private _itemsMap: Map<number, IBroadcast> = new Map()
  private _isEmptyState = true
  private _loadingEmptyState = true

  constructor(private _entities: EntitiesStore) {
    makeAutoObservable(this)
  }

  get isEmptyState() {
    return this._isEmptyState
  }

  get loadingEmptyState() {
    return this._loadingEmptyState
  }

  addItem = (item: IResponseBroadcast) => {
    if (item.status !== 'recurring_template' && item.number !== null) {
      this._entities.numbersStore.addItem(item.number)
    }
    this._itemsMap.set(item.id, this.getBroadcastByResponse(item))
  }

  getBroadcastByResponse = (
    item: IResponseBroadcast | IResponseRecurringBroadcastById
  ): IBroadcast => {
    if (item.status === 'recurring_template') {
      return new BroadcastRecurring(item, this._entities)
    } else {
      return new BroadcastOneTime(item, this._entities)
    }
  }

  getItem = (id: number) => {
    this._itemsMap.get(id)
  }

  getItemById = async (id: number | string) => BroadcastApi.getBroadcastById(id)

  checkEmptyStateBroadcast = async (fromAllPage: boolean) => {
    if (fromAllPage) {
      return
    }
    try {
      const { data } = await BroadcastApi.getBroadcasts({
        type: 'all',
        length: 10,
        page: 1,
      })
      this.setIsEmptyState(!data.data.length)
    } catch (e) {
      logger.error(e)
    } finally {
      this.setLoadingEmptyState(false)
    }
  }
  setIsEmptyState = (value: boolean) => {
    this._isEmptyState = value
    this._loadingEmptyState = false
  }

  setLoadingEmptyState = (value: boolean) => {
    this._loadingEmptyState = value
  }

  private _delayBroadcastMap: Map<
    number,
    {
      resolve: (res: IResponseBroadcastById) => void
      reject: (e: unknown) => void
      promise: Promise<IResponseBroadcastById>
      pending: boolean
    }
  > = new Map()
  getDelayItemById = async (id: number) => {
    const current = this._delayBroadcastMap.get(id)
    if (current) {
      return await current.promise
    }
    let resolve: (res: IResponseBroadcastById) => void
    let reject: (e: unknown) => void
    const promise = new Promise<IResponseBroadcastById>((resolvePromise, rejectPromise) => {
      resolve = resolvePromise
      reject = rejectPromise
    })
    runInAction(() => {
      this._delayBroadcastMap.set(id, { resolve, reject, promise, pending: false })
    })
    this._loadGetDelayItemById()
    return await promise
  }
  private _loadGetDelayItemById = debounce(async () => {
    const idsForLoad: number[] = []
    this._delayBroadcastMap.forEach((value, key) => {
      if (!value.pending) {
        idsForLoad.push(key)
        runInAction(() => {
          value.pending = true
        })
      }
    })
    try {
      if (!idsForLoad.length) return

      const { data } = await BroadcastApi.getBroadcastsByIds(idsForLoad)
      runInAction(() => {
        data.forEach((broadcast) => {
          this._delayBroadcastMap.get(broadcast.id)?.resolve(broadcast)
          this._delayBroadcastMap.delete(broadcast.id)
        })
      })
    } catch (e) {
      runInAction(() => {
        idsForLoad.forEach((id) => {
          this._delayBroadcastMap.get(id)?.reject(e)
          this._delayBroadcastMap.delete(id)
        })
      })

      logger.error(e)
    }
  }, 500)
}
